import { BrandChip } from '@/components/BrandChip';
import { BrandSearchList } from '@/components/BrandSearchList';
import { EquipmentTypeBadge } from '@/components/badges/EquipmentTypeBadge';
import { EquipmentTypeField } from '@/components/EquipmentTypeSelect';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useDebouncedCallback } from '@/lib/useDebouncedCallback';
import { allBrandsState, equipmentTypeFamiliesState, modelSpellingState, modelState } from '@/state';
import { useAsyncAtomValue, useLoadableAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import { joinBrandOf, modelJoinFacts, spellingKeyFor } from '@rock/models';
import type { Brand, BrandId, EquipmentTypeId, ModelId, ModelSpellingKey } from '@rock/types';
import { normalizeIdentifier, normalizeNameKey } from '@rock/util';
import { Plus } from 'lucide-react';
import { type FC, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { addModel } from './addModelApi';
import { brandsById } from './modelBrand';
import { JoinFactTags } from './ModelMatchingPanels';
import { liveBrandOf, modelJoinBrand } from './modelJoin';

const log = logger('AddModelDialog');

const OUTCOME_TOASTS = {
    exists: { title: 'is already in the catalog', description: 'Opening it. Nothing on it was changed.' },
    archived: {
        title: 'is in the catalog, archived',
        description: 'Opening it. Unarchive it there rather than adding a duplicate.',
    },
};

type SubjectKind = 'model-number' | 'product-name';

/**
 * Add a model nobody owns yet, so its documents have somewhere to live. The models already filed under
 * the number show as it is typed, judged the way the join will judge the new one; one the join would hand
 * this type and brand is opened instead of created, and nothing on it changes.
 */
export const AddModelDialog: FC<{ open: boolean; onOpenChange: (open: boolean) => void }> = ({
    open,
    onOpenChange,
}) => {
    const navigate = useNavigate();
    const brands = useAsyncAtomValue(allBrandsState, []);
    const lookup = useMemo(() => brandsById(brands), [brands]);
    const [kind, setKind] = useState<SubjectKind>('model-number');
    const [value, setValue] = useState('');
    const [typeId, setTypeId] = useState<EquipmentTypeId>();
    const [brand, setBrand] = useState<Brand>();
    const [findDocuments, setFindDocuments] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [ambiguous, setAmbiguous] = useState<BrandId[]>();
    const [previewKey, setPreviewKey] = useState<ModelSpellingKey>();
    const schedulePreview = useDebouncedCallback(setPreviewKey);
    const canSubmit = !!spellingKeyOf(kind, value) && !!typeId && !submitting;

    const changeSubject = (nextKind: SubjectKind, nextValue: string) => {
        setKind(nextKind);
        setValue(nextValue);
        schedulePreview(spellingKeyOf(nextKind, nextValue));
    };
    const changeBrand = (next: Brand | undefined) => {
        setBrand(next);
        setAmbiguous(undefined);
    };

    const submit = async () => {
        if (!canSubmit || !typeId) return;
        setSubmitting(true);
        setAmbiguous(undefined);
        try {
            const trimmed = value.trim();
            const outcome = await addModel({
                subject:
                    kind === 'model-number'
                        ? { kind, modelNumber: trimmed }
                        : { kind: 'product-name', productName: trimmed },
                typeId,
                brandId: brand?.id ?? null,
                findDocuments,
            });
            if (outcome.status === 'ambiguous') {
                setAmbiguous(outcome.brandIds);
                return;
            }
            if (outcome.status === 'created') {
                toast.success(`Model ${trimmed} added`);
            } else {
                const message = OUTCOME_TOASTS[outcome.status];
                toast.info(`${trimmed} ${message.title}`, { description: message.description });
            }
            onOpenChange(false);
            navigate(`/products/models/${encodeURIComponent(outcome.modelId)}`);
        } catch (err) {
            log.error('Failed to add model', { err });
            toast.error('Unable to add model', {
                description: err instanceof Error ? err.message : 'An unexpected error occurred.',
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Add model</DialogTitle>
                    <DialogDescription>
                        A catalog model before anyone owns one, so its documents can be published on it.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-3 py-2">
                    <div className="flex gap-2">
                        <Input
                            placeholder={kind === 'model-number' ? 'Model number (e.g. WRF555SDFZ)' : 'Product name'}
                            value={value}
                            onChange={(event) => changeSubject(kind, event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && canSubmit) void submit();
                            }}
                            disabled={submitting}
                            autoFocus
                        />
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() =>
                                changeSubject(kind === 'model-number' ? 'product-name' : 'model-number', value)
                            }
                            disabled={submitting}
                        >
                            {kind === 'model-number' ? 'Use a product name' : 'Use a model number'}
                        </Button>
                    </div>
                    <EquipmentTypeField value={typeId} onChange={setTypeId} disabled={submitting} />
                    <BrandField brands={brands} brand={brand} onChange={changeBrand} disabled={submitting} />
                    <div className="flex items-center gap-2 text-sm">
                        <Checkbox
                            id="add-model-find-documents"
                            checked={findDocuments}
                            onCheckedChange={(checked) => setFindDocuments(checked === true)}
                            disabled={submitting}
                        />
                        <label htmlFor="add-model-find-documents">Look for its documents</label>
                    </div>
                    {!findDocuments && (
                        <div className="section-description">
                            The model is parked as “Can’t find a manual”. Rock searches only if a home adds it or a
                            brand is linked before a document is published.
                        </div>
                    )}
                    {previewKey && (
                        <AlreadyFiled spellingKey={previewKey} typeId={typeId} brand={brand} lookup={lookup} />
                    )}
                    {ambiguous && <AmbiguousBrands brandIds={ambiguous} lookup={lookup} />}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button onClick={submit} disabled={!canSubmit}>
                        {submitting ? 'Adding…' : 'Add model'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

/** The brand as a chip, or a button that opens the brand picker in its own dialog. */
const BrandField: FC<{
    brands: Brand[];
    brand: Brand | undefined;
    onChange: (brand: Brand | undefined) => void;
    disabled: boolean;
}> = ({ brands, brand, onChange, disabled }) => {
    const [pickerOpen, setPickerOpen] = useState(false);
    return (
        <div className="flex flex-wrap items-center gap-2">
            {brand ? (
                <BrandChip brand={brand} onRemove={disabled ? undefined : () => onChange(undefined)} />
            ) : (
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={disabled}
                    onClick={() => setPickerOpen(true)}
                >
                    <Plus className="h-3.5 w-3.5" />
                    Pick a brand
                </Button>
            )}
            {!brand && (
                <span className="section-description">Documents and recalls reach a model through its brand.</span>
            )}
            <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Pick a brand</DialogTitle>
                        <DialogDescription>The brand this model is.</DialogDescription>
                    </DialogHeader>
                    {pickerOpen && (
                        <BrandSearchList
                            brands={brands}
                            onSelect={(picked) => {
                                onChange(picked);
                                setPickerOpen(false);
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

/** The models already filed under the number, with how each sits beside the type and brand picked here. */
const AlreadyFiled: FC<{
    spellingKey: ModelSpellingKey;
    typeId?: EquipmentTypeId;
    brand?: Brand;
    lookup: Map<BrandId, Brand>;
}> = ({ spellingKey, typeId, brand, lookup }) => {
    const [projection] = useLoadableAtomValue(useMemo(() => modelSpellingState(spellingKey), [spellingKey]));
    const entries = projection?.entries ?? [];
    if (entries.length === 0) return null;
    return (
        <div className="space-y-1.5">
            <div className="text-sm font-medium">Already in the catalog under this number</div>
            {entries.map((entry) => (
                <FiledModelRow
                    key={entry.modelId}
                    modelId={entry.modelId}
                    typeId={typeId}
                    brand={brand}
                    lookup={lookup}
                />
            ))}
        </div>
    );
};

const FiledModelRow: FC<{
    modelId: ModelId;
    typeId?: EquipmentTypeId;
    brand?: Brand;
    lookup: Map<BrandId, Brand>;
}> = ({ modelId, typeId, brand, lookup }) => {
    const model = useAsyncAtomValue(useMemo(() => modelState(modelId), [modelId]));
    const families = useAsyncAtomValue(equipmentTypeFamiliesState, []);
    if (!model) return <span className="font-mono text-xs text-muted-foreground">{modelId}</span>;
    const facts =
        typeId &&
        modelJoinFacts(
            { model, brand: modelJoinBrand(model, lookup) },
            { typeId, brand: joinBrandOf(brand) },
            families,
        );
    return (
        <Link
            to={`/products/models/${encodeURIComponent(model.id)}`}
            className="flex flex-wrap items-center gap-2 rounded-md border p-2 text-sm hover:bg-muted/50"
        >
            <span className="font-mono font-medium">{model.modelDisplay ?? model.modelCanonical}</span>
            <span className="text-muted-foreground">
                {liveBrandOf(model.brandId, lookup)?.canonicalName ?? 'no brand'}
            </span>
            <EquipmentTypeBadge typeId={model.typeId} />
            {model.archivedAt && <Badge variant="secondary">Archived</Badge>}
            {facts && (
                <span className="ml-auto flex flex-wrap gap-1">
                    <JoinFactTags facts={facts} />
                </span>
            )}
        </Link>
    );
};

const AmbiguousBrands: FC<{ brandIds: BrandId[]; lookup: Map<BrandId, Brand> }> = ({ brandIds, lookup }) => {
    const names = brandIds.map((id) => liveBrandOf(id, lookup)?.canonicalName ?? id);
    return (
        <div className="text-sm text-destructive">
            This number is filed under several brands ({names.join(', ')}). Pick the brand this model is.
        </div>
    );
};

/** The key the server will look the number up under, folded the way `addModel` folds it. */
function spellingKeyOf(kind: SubjectKind, value: string): ModelSpellingKey | undefined {
    const canonical = kind === 'product-name' ? normalizeNameKey(value) : normalizeIdentifier(value);
    return canonical ? spellingKeyFor(kind, canonical) : undefined;
}
